import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../ui/use-toast';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../../lib/supabaseClient';

export const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Processing authentication...');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const guidance = (description: string) => /code.verifier|bad_code_verifier|code challenge/i.test(description)
    ? 'The sign-in email opened in a different browser or browser profile from the one that requested it. Open a new link in the same browser profile, without private/incognito mode.'
    : /otp.expired|expired|invalid|one.time.token/i.test(description)
    ? 'This one-time link is invalid or has already been used. Request a fresh link, then open it in the same browser profile.'
    : description;

  useEffect(() => {
    const handleOAuthCallback = async () => {
      try {
        // Check for error parameters in URL
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');
        
        if (error) {
          setStatus('error');
          setMessage(guidance(errorDescription || `Authentication failed: ${error}`));
          toast({
            title: 'Authentication Failed',
            description: errorDescription || error,
            variant: 'destructive',
          });
          
          // Keep actionable errors visible; don't erase them with a timed redirect.
          return;
        }

        const hash = new URLSearchParams(window.location.hash.slice(1));
        const hashError = hash.get('error_description') || hash.get('error');
        if (hashError) {
          setStatus('error');
          setMessage(guidance(hashError));
          toast({
            title: 'Authentication Failed',
            description: hashError,
            variant: 'destructive',
          });
          return;
        }

        // PKCE magic links exchange the code during client init. Give that a moment.
        let session: Session | null = null;
        let authError: { message: string } | null = null;
        for (let attempt = 0; attempt < 8; attempt++) {
          const result = await supabase.auth.getSession();
          authError = result.error;
          session = result.data.session;
          if (authError || session?.user) break;
          await new Promise((resolve) => setTimeout(resolve, 250));
        }

        if (authError) {
          console.error('Auth callback error:', authError);
          setStatus('error');
          setMessage(guidance(authError.message));
          toast({
            title: 'Authentication Failed',
            description: authError.message,
            variant: 'destructive',
          });
          
          return;
        }

        if (session?.user) {
          const next = sessionStorage.getItem('og01_auth_next') || '/dashboard';
          sessionStorage.removeItem('og01_auth_next');
          setStatus('success');
          setMessage('Authentication successful! Redirecting...');
          toast({
            title: 'Welcome!',
            description: 'You have successfully signed in.',
          });
          
          setTimeout(() => {
            navigate(next, { replace: true });
          }, 1200);
        } else {
          setStatus('error');
          setMessage('No active session found. Please try signing in again.');
          
          // Keep diagnostic guidance visible until the user chooses their next step.
        }
      } catch (error) {
        console.error('OAuth callback processing error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred during authentication.');
        toast({
          title: 'Authentication Error',
          description: 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        });
        
        setTimeout(() => {
          navigate('/auth/login');
        }, 3000);
      }
    };

    handleOAuthCallback();
  }, [searchParams, navigate, toast]);

  const getStatusIcon = () => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-8 w-8 animate-spin text-blue-500" />;
      case 'success':
        return <CheckCircle className="h-8 w-8 text-green-500" />;
      case 'error':
        return <XCircle className="h-8 w-8 text-red-500" />;
      default:
        return <Loader2 className="h-8 w-8 animate-spin text-blue-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'loading':
        return 'text-blue-600';
      case 'success':
        return 'text-green-600';
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            {getStatusIcon()}
          </div>
          <CardTitle className={`text-xl ${getStatusColor()}`}>
            {status === 'loading' && 'Processing Authentication'}
            {status === 'success' && 'Authentication Successful!'}
            {status === 'error' && 'Authentication Failed'}
          </CardTitle>
          <CardDescription className="text-center">
            {message}
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <div className="text-sm text-muted-foreground">
            {status === 'loading' && 'Please wait while we complete your sign-in...'}
            {status === 'success' && 'Redirecting you to the platform...'}
            {status === 'error' && <span><Link className="underline" to="/auth/login">Back to sign in</Link> · <Link className="underline" to="/auth/forgot-password">Reset password</Link></span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}; 